import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Clock, Trophy, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { renderMarkdownWithMath } from "@/lib/markdown-math";
import { cn } from "@/lib/cn";
import { useStartAssignment, useSubmitAssignment, useTest } from "@/hooks/useTests";
import type { AssignmentResult, TestQuestion } from "@/lib/api-types";

function useCountdown(deadlineIso: string | null) {
  // Derived directly from deadlineIso + Date.now() on every render (not a
  // useState seeded while deadlineIso was still null) -- a lagging-by-one-render
  // stale 0 here reads as "time's up" to the auto-submit effect below and would
  // fire an empty-answer submit the instant a test starts. `tick` only forces
  // a re-render every second so the displayed countdown ticks down; it never
  // gates the actual value.
  const [, tick] = useState(0);
  useEffect(() => {
    if (!deadlineIso) return;
    const id = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [deadlineIso]);
  if (!deadlineIso) return 0;
  return Math.max(0, new Date(deadlineIso).getTime() - Date.now());
}

function formatCountdown(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

/**
 * Exam mode -- deliberately NOT the quiz-mode experience (QuizPage): no
 * per-question reveal, a countdown re-synced from the server-authoritative
 * `server_deadline` (set once at /start, never trusted from the client),
 * question palette navigation, submit-confirm dialog, and auto-submit when
 * time runs out. The AI tutor FAB is suppressed for this route in
 * StudyShell -- see the comment there.
 */
export function TestTakePage() {
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const navigate = useNavigate();
  const startAssignment = useStartAssignment();
  const submitAssignment = useSubmitAssignment();

  const [phase, setPhase] = useState<"loading" | "error" | "taking" | "submitted">("loading");
  const [questions, setQuestions] = useState<TestQuestion[]>([]);
  const [deadline, setDeadline] = useState<string | null>(null);
  const [testId, setTestId] = useState<string | null>(null);
  const [idx, setIdx] = useState(0);
  const [picks, setPicks] = useState<Record<number, number | null>>({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submittedResult, setSubmittedResult] = useState<AssignmentResult | null>(null);
  const submittingRef = useRef(false);
  // Only auto-submit once the countdown has actually been observed running
  // (> 0). Guards against a deadline that is already in the past on mount --
  // a stale STARTED attempt or a client clock ahead of the server -- instantly
  // submitting an empty test and locking the student out at 0%.
  const timerWasLiveRef = useRef(false);

  useEffect(() => {
    if (!assignmentId) return;
    let cancelled = false;
    startAssignment.mutate(assignmentId, {
      onSuccess: (data) => {
        if (cancelled || !data) return;
        if (data.status === "COMPLETED") {
          setSubmittedResult(data.result);
          setTestId(data.test_id);
          setPhase("submitted");
          return;
        }
        setQuestions(data.test?.questions ?? []);
        setDeadline(data.server_deadline);
        setTestId(data.test_id);
        setPhase("taking");
      },
      onError: () => setPhase("error"),
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentId]);

  const msLeft = useCountdown(phase === "taking" ? deadline : null);

  const onSubmit = async (autoSubmit: boolean) => {
    if (!assignmentId || submittingRef.current) return;
    submittingRef.current = true;
    try {
      const answers = questions.map((q) => ({ question_id: q.id, picked: picks[q.id] ?? null }));
      const result = await submitAssignment.mutateAsync({ assignmentId, answers, auto_submit: autoSubmit });
      if (result) {
        setSubmittedResult(result.result);
        setPhase("submitted");
      }
    } catch {
      toast.error("Couldn't submit -- check your connection and try again.");
      submittingRef.current = false;
    }
  };

  // Auto-submit the moment the server-authoritative deadline passes -- but only
  // after the timer was seen ticking with real time left, so a deadline that is
  // already expired on mount can't instant-submit an empty attempt.
  useEffect(() => {
    if (phase !== "taking") return;
    if (msLeft > 0) {
      timerWasLiveRef.current = true;
      return;
    }
    if (deadline && timerWasLiveRef.current) void onSubmit(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msLeft, phase]);

  // Warn on tab close mid-attempt -- losing an in-progress timed test is costly.
  useEffect(() => {
    if (phase !== "taking") return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [phase]);

  if (phase === "loading") {
    return (
      <div className="flex-1 grid place-items-center text-sm text-text-muted py-20">Starting your test…</div>
    );
  }
  if (phase === "error") {
    return (
      <div className="flex-1 grid place-items-center text-sm text-text-muted py-20">
        Couldn't start this test. It may not be ready yet.
      </div>
    );
  }
  if (phase === "submitted") {
    return <TestResultsView testId={testId} result={submittedResult} onDone={() => navigate("/tests")} />;
  }

  const current = questions[idx];
  const answeredCount = Object.values(picks).filter((v) => v != null).length;

  return (
    <div className="w-full max-w-3xl mx-auto px-4 sm:px-6 pt-4 pb-[calc(env(safe-area-inset-bottom)+1.5rem)]">
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs text-text-muted">
          {answeredCount}/{questions.length} answered
        </span>
        <span
          className={cn(
            "inline-flex items-center gap-1.5 text-sm font-semibold tabular-nums",
            msLeft < 60_000 ? "text-danger" : "text-text",
          )}
        >
          <Clock className="h-4 w-4" />
          {formatCountdown(msLeft)}
        </span>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-5">
        {questions.map((q, i) => {
          const answered = picks[q.id] != null;
          return (
            <button
              key={q.id}
              type="button"
              onClick={() => setIdx(i)}
              className={cn(
                "h-8 w-8 rounded-md text-xs font-medium border transition-colors",
                i === idx
                  ? "border-brand bg-brand text-white"
                  : answered
                    ? "border-success/40 bg-success/10 text-success"
                    : "border-border text-text-muted hover:bg-surface-2",
              )}
            >
              {i + 1}
            </button>
          );
        })}
      </div>

      {current ? (
        <Card className="p-5 sm:p-7">
          <div
            className="text-base sm:text-lg leading-relaxed text-text [&_p]:my-1.5 [&_.katex-display]:my-2"
            dangerouslySetInnerHTML={{ __html: renderMarkdownWithMath(current.question) }}
          />
          <ol className="mt-5 space-y-2.5">
            {current.options.map((opt, oi) => {
              const isPick = picks[current.id] === oi;
              return (
                <li key={oi}>
                  <button
                    type="button"
                    onClick={() => setPicks((prev) => ({ ...prev, [current.id]: oi }))}
                    className={cn(
                      "w-full text-left rounded-xl border px-4 py-3 sm:py-3.5 flex items-start gap-3 transition-colors",
                      isPick ? "border-brand bg-brand-soft" : "border-border hover:bg-surface-2",
                    )}
                  >
                    <span
                      className={cn(
                        "grid place-items-center h-6 w-6 rounded-md text-xs font-semibold flex-shrink-0 mt-0.5",
                        isPick ? "bg-brand text-white" : "bg-surface-2 text-text-muted",
                      )}
                    >
                      {String.fromCharCode(65 + oi)}
                    </span>
                    <span
                      className="flex-1 text-[15px] leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: renderMarkdownWithMath(opt) }}
                    />
                  </button>
                </li>
              );
            })}
          </ol>
        </Card>
      ) : null}

      <div className="mt-5 flex items-center justify-between gap-3">
        <Button variant="ghost" disabled={idx === 0} onClick={() => setIdx((i) => Math.max(0, i - 1))}>
          Previous
        </Button>
        {idx < questions.length - 1 ? (
          <Button onClick={() => setIdx((i) => Math.min(questions.length - 1, i + 1))}>Next</Button>
        ) : (
          <Button onClick={() => setConfirmOpen(true)}>Submit test</Button>
        )}
      </div>

      {confirmOpen ? (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4">
          <Card className="max-w-sm w-full p-6">
            <h3 className="font-display text-lg font-semibold">Submit this test?</h3>
            <p className="mt-1.5 text-sm text-text-muted">
              You've answered {answeredCount} of {questions.length} questions. You can't change your answers
              after submitting.
            </p>
            <div className="mt-5 flex gap-2 justify-end">
              <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
                Keep going
              </Button>
              <Button
                onClick={() => {
                  setConfirmOpen(false);
                  void onSubmit(false);
                }}
                loading={submitAssignment.isPending}
              >
                Submit
              </Button>
            </div>
          </Card>
        </div>
      ) : null}
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div className="rounded-lg border border-border bg-surface-2 px-3 py-3">
      <div className={cn("font-display text-2xl font-semibold tabular-nums", tone)}>{value}</div>
      <div className="text-[11px] uppercase tracking-[0.14em] text-text-subtle mt-1">{label}</div>
    </div>
  );
}

function TestResultsView({
  testId,
  result,
  onDone,
}: {
  testId: string | null;
  result: AssignmentResult | null;
  onDone: () => void;
}) {
  const test = useTest(testId ?? undefined);

  if (!result) {
    return (
      <div className="flex-1 grid place-items-center text-sm text-text-muted py-20">No result to show.</div>
    );
  }

  const questions = test.data?.questions ?? [];
  const pickedByQid = new Map(result.per_question.map((p) => [p.question_id, p.picked]));
  const celebrate = result.percent >= 80;
  const tone = result.percent >= 80 ? "text-success" : result.percent >= 60 ? "text-brand" : "text-danger";

  return (
    <div className="w-full max-w-3xl mx-auto px-4 sm:px-6 pt-4 pb-[calc(env(safe-area-inset-bottom)+2rem)]">
      <Card
        className={cn(
          "relative rounded-2xl border p-6 sm:p-8 text-center",
          celebrate ? "border-success/40" : "border-border",
        )}
      >
        <div
          className={cn(
            "mx-auto grid place-items-center h-12 w-12 rounded-full mb-3",
            celebrate ? "bg-success/15 text-success" : "bg-brand-soft text-brand",
          )}
        >
          <Trophy className="h-6 w-6" />
        </div>
        <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">Test complete</h2>

        <div className="mt-6 grid grid-cols-3 gap-3 sm:gap-4">
          <Stat label="Score" value={`${result.percent}%`} tone={tone} />
          <Stat label="Correct" value={`${result.score}/${result.total}`} />
          <Stat
            label="Time"
            value={result.time_taken_seconds != null ? `${Math.round(result.time_taken_seconds / 60)}m` : "--"}
          />
        </div>

        <Button className="mt-6" onClick={onDone}>
          Back to tests
        </Button>
      </Card>

      {questions.length > 0 ? (
        <>
          <h3 className="font-display text-base font-semibold tracking-tight mt-8 mb-3">Question review</h3>
          <ol className="space-y-3">
            {questions.map((q) => {
              const picked = pickedByQid.get(q.id) ?? null;
              const isRight = picked === q.correctAnswer;
              return (
                <li key={q.id} className="rounded-xl border border-border bg-surface p-4 sm:p-5">
                  <div className="flex items-start gap-2.5">
                    {isRight ? (
                      <CheckCircle2 className="h-5 w-5 text-success flex-shrink-0 mt-0.5" />
                    ) : (
                      <XCircle className="h-5 w-5 text-danger flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div
                        className="text-sm sm:text-base text-text mt-0.5 [&_p]:my-0.5"
                        dangerouslySetInnerHTML={{ __html: renderMarkdownWithMath(q.question) }}
                      />
                      <div className="mt-2.5 space-y-1.5">
                        {q.options.map((opt, oi) => {
                          const showAsCorrect = oi === q.correctAnswer;
                          const showAsWrong = !isRight && oi === picked;
                          return (
                            <div
                              key={oi}
                              className={cn(
                                "rounded-md px-3 py-2 text-sm",
                                showAsCorrect
                                  ? "bg-success/10 text-text border border-success/30"
                                  : showAsWrong
                                    ? "bg-danger/10 text-text border border-danger/30"
                                    : "bg-surface-2 text-text-muted border border-transparent",
                              )}
                              dangerouslySetInnerHTML={{ __html: renderMarkdownWithMath(opt) }}
                            />
                          );
                        })}
                      </div>
                      {q.explanation ? (
                        <p
                          className="mt-2 text-xs text-text-muted [&_p]:my-0"
                          dangerouslySetInnerHTML={{ __html: renderMarkdownWithMath(q.explanation) }}
                        />
                      ) : null}
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        </>
      ) : null}
    </div>
  );
}
